import {
  Target, Eye, Ruler, BookOpen, Filter, FlaskConical, Heart, Lightbulb,
  FileText, Quote, BarChart3, Scale, HelpCircle, GitFork, CircleHelp,
  ArrowRight, X, Scissors, Wrench, Link2, ShieldAlert, RefreshCw,
  Sparkles, User, Download, GitBranch, Cpu, type LucideIcon,
} from "lucide-react";
import type { NodeType, RelationType, Provenance, CruxCategory, ForkIntent } from "@/types";

// ---------------------------------------------------------------- node types
export const NODE_TYPES: Record<
  NodeType,
  { label: string; short: string; icon: LucideIcon; color: string; tone: string }
> = {
  claim:            { label: "Claim",            short: "CLM", icon: Target,       color: "var(--color-primary)",   tone: "primary" },
  hidden_warrant:   { label: "Hidden Warrant",   short: "WAR", icon: Eye,          color: "var(--color-qualify)",   tone: "violet" },
  criterion:        { label: "Criterion",        short: "CRT", icon: Ruler,        color: "var(--color-define)",    tone: "blue" },
  definition:       { label: "Definition",       short: "DEF", icon: BookOpen,     color: "var(--color-define)",    tone: "blue" },
  scope_condition:  { label: "Scope Condition",  short: "SCP", icon: Filter,       color: "var(--color-qualify)",   tone: "violet" },
  empirical_claim: { label: "Empirical Claim",  short: "EMP", icon: FlaskConical, color: "var(--color-evidence)",  tone: "cyan" },
  value_claim:      { label: "Value Claim",      short: "VAL", icon: Heart,        color: "var(--color-value)",     tone: "rose" },
  interpretation:   { label: "Interpretation",   short: "INT", icon: Lightbulb,    color: "var(--color-reframe)",   tone: "purple" },
  evidence:         { label: "Evidence",         short: "EVI", icon: FileText,     color: "var(--color-evidence)",  tone: "cyan" },
  example:          { label: "Example",          short: "EXM", icon: Quote,        color: "var(--color-ink-muted)", tone: "neutral" },
  measurement:      { label: "Measurement",      short: "MSR", icon: BarChart3,    color: "var(--color-evidence)",  tone: "cyan" },
  terminal_value:   { label: "Terminal Value",  short: "TVL", icon: Scale,        color: "var(--color-value)",     tone: "rose" },
  question:         { label: "Question",         short: "QST", icon: HelpCircle,   color: "var(--color-secondary)",  tone: "amber" },
  bridge:           { label: "Bridge",           short: "BRG", icon: GitFork,      color: "var(--color-primary)",   tone: "primary" },
  unresolved_crux:  { label: "Unresolved Crux",  short: "CRX", icon: CircleHelp,    color: "var(--color-secondary)",  tone: "amber" },
};

export function nodeMeta(t: NodeType) { return NODE_TYPES[t] ?? NODE_TYPES.claim; }

// ---------------------------------------------------------------- edge types
// accessibility rule (§9.5): never color alone. every edge also has a
// line style, an arrow kind, and a short label, plus an icon.
export const EDGE_TYPES: Record<
  RelationType,
  {
    label: string;
    verb: string; // "supports", "rebuts" ...
    icon: LucideIcon;
    color: string;
    stroke: "solid" | "dashed" | "dotted";
    strokeWidth: number;
    arrow: "default" | "thin" | "blunt" | "open" | "none";
    description: string; // plain-language tooltip
  }
> = {
  supports:     { label: "Supports",     verb: "supports",     icon: ArrowRight,  color: "var(--color-support)",     stroke: "solid",  strokeWidth: 2,   arrow: "default", description: "Source gives a reason to accept the target." },
  rebuts:       { label: "Rebuts",       verb: "rebuts",       icon: X,           color: "var(--color-rebut)",       stroke: "solid",  strokeWidth: 2,   arrow: "default", description: "Source says the target is false, weak, or overstated." },
  undercuts:    { label: "Undercuts",    verb: "undercuts",    icon: Scissors,    color: "var(--color-undercut)",    stroke: "dashed", strokeWidth: 2,   arrow: "blunt",   description: "Attacks the inference between two nodes — not either node by itself." },
  qualifies:    { label: "Qualifies",    verb: "qualifies",    icon: Wrench,     color: "var(--color-qualify)",     stroke: "dashed", strokeWidth: 1.75, arrow: "open",    description: "Narrows the scope of the target." },
  defines:      { label: "Defines",      verb: "defines",      icon: BookOpen,    color: "var(--color-define)",      stroke: "dotted", strokeWidth: 1.75, arrow: "thin",    description: "Defines a key term in the target." },
  evidences:    { label: "Evidences",    verb: "evidences",    icon: Link2,       color: "var(--color-evidence)",    stroke: "solid",  strokeWidth: 1.75, arrow: "thin",    description: "Source is evidence for the target." },
  credibility:  { label: "Credibility",  verb: "challenges",   icon: ShieldAlert, color: "var(--color-credibility)", stroke: "dashed", strokeWidth: 1.75, arrow: "blunt",   description: "Challenges the source, authority, or incentive behind a node." },
  reframes:     { label: "Reframes",     verb: "reframes",     icon: RefreshCw,   color: "var(--color-reframe)",     stroke: "dotted", strokeWidth: 2,   arrow: "open",    description: "Changes the interpretation or criterion by which the target should be judged." },
};

export function edgeMeta(t: RelationType) { return EDGE_TYPES[t] ?? EDGE_TYPES.supports; }

export const ALL_EDGE_TYPES = Object.keys(EDGE_TYPES) as RelationType[];

// ---------------------------------------------------------------- provenance
export const PROVENANCE: Record<
  Provenance,
  { label: string; short: string; icon: LucideIcon; color: string; note: string }
> = {
  user:         { label: "User-written",        short: "user",    icon: User,       color: "var(--color-primary)",   note: "Written by a human." },
  ai_inferred:  { label: "AI-inferred",          short: "inferred", icon: Sparkles,   color: "var(--color-inferred)",  note: "Suggested by AI. Not yet accepted — treat as a proposal." },
  ai_confirmed: { label: "AI-confirmed",         short: "confirmed", icon: Sparkles,  color: "var(--color-primary)",   note: "AI suggestion accepted by a human." },
  imported:     { label: "Imported",             short: "imported", icon: Download,   color: "var(--color-imported)",  note: "Brought in from another strain or source." },
  forked:       { label: "Forked",               short: "forked",  icon: GitBranch,   color: "var(--color-forked)",    note: "Carried over from a parent version." },
  system:       { label: "System",              short: "system",  icon: Cpu,        color: "var(--color-ink-muted)", note: "Generated by the system." },
};

export function provenanceMeta(p: Provenance) { return PROVENANCE[p] ?? PROVENANCE.user; }

// ---------------------------------------------------------------- crux categories
export const CRUX_CATEGORIES: Record<CruxCategory, string> = {
  load_bearing_assumption: "Load-bearing assumption",
  disputed_fact: "Disputed fact",
  disputed_definition: "Disputed definition",
  disputed_value_criterion: "Disputed value criterion",
  measurement_dispute: "Measurement dispute",
  causal_inference: "Causal inference",
  scope_condition: "Scope condition",
  evidence_validity: "Evidence validity issue",
  frequently_forked: "Frequently forked point",
  mind_changing: "Mind-changing point",
};

// ---------------------------------------------------------------- fork intents
export const FORK_INTENTS: Record<ForkIntent, { label: string; color: string; description: string }> = {
  strengthen:      { label: "Strengthen",      color: "var(--color-support)",   description: "Add stronger support or fix a weak point." },
  rebut:            { label: "Rebut",           color: "var(--color-rebut)",     description: "Attack a claim or inference." },
  narrow:           { label: "Narrow",          color: "var(--color-qualify)",   description: "Tighten the scope or population." },
  broaden:          { label: "Broaden",         color: "var(--color-define)",    description: "Generalize beyond the original scope." },
  reframe:          { label: "Reframe",         color: "var(--color-reframe)",   description: "Change the criterion or interpretation." },
  update_evidence:  { label: "Update evidence", color: "var(--color-evidence)",  description: "Add, replace, or correct evidence." },
  steelman:         { label: "Steelman",        color: "var(--color-primary)",   description: "Build the strongest version of the idea." },
  mutation:         { label: "Mutate",          color: "var(--color-ink-muted)", description: "Unrelated change (clearly marked)." },
};

// ---------------------------------------------------------------- reaction chips (§10.4)
export const NODE_REACTIONS = [
  { type: "agree", label: "Agree", emoji: "✓", tone: "ok" },
  { type: "disagree", label: "Disagree", emoji: "✕", tone: "danger" },
  { type: "unsure", label: "Unsure", emoji: "?", tone: "muted" },
  { type: "needs_evidence", label: "Needs evidence", emoji: "!", tone: "warn" },
  { type: "fair_point", label: "Fair point", emoji: "◆", tone: "primary" },
  { type: "changed_mind", label: "Changed my mind", emoji: "↻", tone: "inferred" },
  { type: "too_broad", label: "Too broad", emoji: "≠", tone: "amber" },
  { type: "good_crux", label: "Good crux", emoji: "★", tone: "amber" },
] as const;

export const EDGE_REACTIONS = [
  { type: "follows", label: "Follows", emoji: "✓", tone: "ok" },
  { type: "does_not_follow", label: "Doesn't follow", emoji: "✕", tone: "danger" },
  { type: "needs_warrant", label: "Needs warrant", emoji: "!", tone: "warn" },
  { type: "strong_support", label: "Strong support", emoji: "▲", tone: "ok" },
  { type: "weak_support", label: "Weak support", emoji: "▽", tone: "muted" },
  { type: "good_undercut", label: "Good undercut", emoji: "✂", tone: "amber" },
  { type: "source_mismatch", label: "Source mismatch", emoji: "≠", tone: "danger" },
] as const;

// ---------------------------------------------------------------- feed status badges
export const STRAIN_BADGE_META: Record<string, { label: string; tone: string }> = {
  new:              { label: "new",            tone: "info" },
  hot_crux:         { label: "hot crux",       tone: "amber" },
  needs_evidence:   { label: "needs evidence", tone: "warn" },
  fork_storm:       { label: "fork storm",     tone: "purple" },
  bridge_candidate: { label: "bridge candidate", tone: "primary" },
  rising:           { label: "rising",         tone: "ok" },
  contested:        { label: "contested",      tone: "danger" },
};

// ---------------------------------------------------------------- helpers
export function statusLabel(status: string): string {
  return status.replace(/_/g, " ");
}

export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.floor((now - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}
